import { css } from '@emotion/core';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { AngularServicesContext } from 'react-app';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { gray7 } from 'styles/global_defaults/colors';
import { screenXsMax } from 'styles/global_defaults/media-queries';
import { lectureLeftPanelZIndex } from 'styles/global_defaults/scaffolding';
import AddComponentTopLevel from './add-component-top-level';
import AddContentComponent from './add-content-component';
import CourseOutlineNavigation from './course-outline-navigation';
import LeftPanelPlaceHolder from './left-panel-placeholder';

type LecturePageLeftPanelProps = {
  // When true, this panel will float above the page content with absolute positioning at a fixed width
  floatAbove: boolean,
};

/** The left-side panel (in left-to-right languages) that contains UIs for navigating through a course via the outline
 * and for adding new lecture components */
export const LecturePageLeftPanel = (props: LecturePageLeftPanelProps) => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${screenXsMax}px)`,
  });

  const styles = css`
    display: flex;
    justify-content: center;
    z-index: ${lectureLeftPanelZIndex};

    width: 25%;
    height: 100%;
    flex-shrink: 0;
    background-color: ${gray7};
    overflow-y: auto;

    ${props.floatAbove && css`
      position: absolute;
      width: ${isMobile ? '100%' : '320px'};
    `}

    /** Large padding offset applied to ensure outline is placed north of the bottom bar */
    padding-bottom: 100px;
    & > * {
      height: max-content;
    }
  `;

  // Determines which specific UI is displayed in this panel. This is updated via the Redux app state in response to URL changes, button
  // clicks, etc
  const leftPanelView = useSelector(state => state.app.lecturePage.leftPanelView);
  const isFullTimelineLoaded = useSelector(state => state.app.lecturePage.isFullTimelineLoaded);

  const ref = useRef(null);
  const params = useLecturePageParams();
  const { $timeout } = useContext(AngularServicesContext);

  /**
   * Scrolling to the top is not working as expected when switching LHS view to
   * add while scrolling initiated on the existing view. Using `scrollToTop`
   * will force scroll to the top of the page when the user clicks on the add
   * component button.
   */
  const scrollToTop = useRef(false);
  /**
   * Scrolling to the current lecture page LHS item. Retrying the scroll if the
   * lecture item is not found in the dom.
   * `scrollIntoView` doesn't scroll as expected here. So implemented the old
   * lecture timeline scroll logic here.
   */
  const tryScroll = useCallback((tries: number) => {
    if (tries < 0) {
      return;
    }

    const scrollPosition = $(`#timeline-lecture-page-${params.lecturePageId}`).position();
    const $timelinePanel = $(ref.current);

    if (scrollPosition) {
      tries = 0;
      $timelinePanel.animate(
        { scrollTop: scrollPosition.top + $timelinePanel.scrollTop() },
        'slow',
        () => {
          if (scrollToTop.current) {
            ref.current.scroll({ top: 0, left: 0, behavior: 'smooth' });
            scrollToTop.current = false;
          }
        },
      );
    } else {
      $timeout(() => {
        tryScroll(tries - 1);
      }, 500);
    }
  }, [$timeout, params.lecturePageId]);

  useEffect(() => {
    if (ref.current && leftPanelView === 'outline') {
      tryScroll(5);
    }
  }, [leftPanelView, tryScroll, isFullTimelineLoaded]);

  useEffect(() => {
    scrollToTop.current = false;

    // Scrolling the LHS list to top when adding a new component.
    if (leftPanelView !== 'outline') {
      scrollToTop.current = true;
      ref.current.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [leftPanelView]);

  return (
    <div css={styles} ref={ref}>
      {leftPanelView === 'outline'
      && (
      <React.Fragment>
        {/* The full (not lite) timeline is needed because we don't have enough info to display completion
        statuses in the lite version */}
        {isFullTimelineLoaded ? <CourseOutlineNavigation /> : <LeftPanelPlaceHolder />}
      </React.Fragment>
      )}

      {leftPanelView === 'add_top_level' && (
        <AddComponentTopLevel />
      )}

      {leftPanelView === 'add_content' && (
        <AddContentComponent />
      )}
    </div>
  );
};

export default LecturePageLeftPanel;


